// Pure rules behind the "ตรวจสอบกับ Shopee" tool of the new BO orders page.
// Spec: docs/specs/2026-10-07-shopee-cancel-reconcile-design.md §3, §4, §11 (F2–F4).
//
//   S1 GET  /api/v1/platform/shopee/cancel-census?time_from&time_to[&shop_id][&format=csv]
//      -> {time_from, time_to, total, summary:{class:n}, orders:[…]}
//   S2 POST /api/v1/platform/shopee/cancel-census/apply {bo_ids, dry_run}
//      -> {dry_run, applied:[…], skipped:[{bo_id, reason}]}
//
// No vue import: this repo's jest cannot mount a .vue file, so OrdersNew.vue
// only renders what lives here and tests/unit/shopeeCancelCensus.spec.js pins it.

/** Every class S1 can answer, in the order the summary chips are drawn. */
export const CENSUS_CLASSES = [
  'stale_cancel_live',
  'missing_cancel',
  'stale_cancel_live_staff',
  'pending_request_bo_cancelled',
  'pending_request',
  'unpaid_or_gone',
  'other',
  'fetch_error',
  'ok_cancelled',
  'ok_live'
];

/** Rows that agree with Shopee — hidden by default (§11 F2 "แสดงทั้งหมด"). */
export const OK_CLASSES = ['ok_cancelled', 'ok_live'];

/** The only classes S2 accepts (§4) — the only rows with a checkbox. */
export const SELECTABLE_CLASSES = ['stale_cancel_live', 'missing_cancel'];

/** Skip reasons S2 can answer (besides a class name: "not repairable now"). */
export const SKIP_REASONS = [
  'skipped_packing',
  'skipped_return_episode',
  'skipped_shopee_cancelled_before',
  'skipped_row_changed',
  'skipped_cancel_not_by_platform',
  'skipped_platform_cancel_already_recorded',
  'not_found',
  'fetch_error',
  'internal_error'
];

/** S2 `applied[].action` values. */
export const APPLY_ACTIONS = ['uncancel', 'cancel', 'platform_status_only'];

/** BE window cap (shopeeCancelCensusMaxDays). */
export const CENSUS_MAX_DAYS = 35;

/** S2 cap per call (shopeeCancelRepairMaxIDs). */
export const APPLY_MAX_IDS = 1000;

const TONES = {
  ok_cancelled: 'success',
  ok_live: 'success',
  stale_cancel_live: 'danger',
  missing_cancel: 'danger',
  stale_cancel_live_staff: 'warning',
  pending_request_bo_cancelled: 'warning',
  pending_request: 'warning',
  unpaid_or_gone: 'info',
  other: 'info',
  fetch_error: 'info'
};

function isKnownClass(cls) {
  return CENSUS_CLASSES.indexOf(cls) >= 0;
}

/** i18n key of a class name; unknown -> the "other" label. */
export function classLabel(cls) {
  return `shopee_census.class_${isKnownClass(cls) ? cls : 'other'}`;
}

/** el-tag `type` of a class: danger = S2 can fix, warning = a person decides. */
export function classTone(cls) {
  return TONES[cls] || 'info';
}

/** True only for the classes S2 repairs. */
export function selectable(cls) {
  return SELECTABLE_CLASSES.indexOf(cls) >= 0;
}

function dayUtc(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(typeof s === 'string' ? s.trim() : '');
  if (!m) return NaN;
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(t);
  // 2026-02-31 rolls over in Date.UTC: reject it.
  if (d.getUTCMonth() !== Number(m[2]) - 1) return NaN;
  return t;
}

/**
 * rangeDays — days covered by a YYYY-MM-DD window, BOTH ends included (the BE
 * reads a date-only time_to as "end of that day"). 2026-10-01..2026-10-07 = 7.
 * 0 for a missing / broken / inverted window.
 */
export function rangeDays(from, to) {
  const a = dayUtc(from);
  const b = dayUtc(to);
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

/**
 * censusRangeError — '' when S1 may be called; 'missing' (no / broken /
 * inverted window) or 'too_long' (> CENSUS_MAX_DAYS, would be a 400).
 */
export function censusRangeError(from, to) {
  const n = rangeDays(from, to);
  if (!n) return 'missing';
  if (n > CENSUS_MAX_DAYS) return 'too_long';
  return '';
}

/** S1 query for a window (dates as typed: the BE takes YYYY-MM-DD). */
export function censusParams(from, to, extra) {
  const p = { time_from: from, time_to: to };
  if (extra && extra.shop_id) p.shop_id = extra.shop_id;
  if (extra && extra.format) p.format = extra.format;
  return p;
}

/**
 * skipReasonLabel — the Thai/English sentence for one S2 skip reason. `t` is
 * the view's $t; without it the i18n key comes back. A class name as reason
 * means "this order is no longer repairable: it is now <class>".
 */
export function skipReasonLabel(reason, t) {
  const tr = typeof t === 'function' ? t : (k, p) => (p ? `${k}${JSON.stringify(p)}` : k);
  if (SKIP_REASONS.indexOf(reason) >= 0) return tr(`shopee_census.skip_${reason}`);
  if (isKnownClass(reason)) return tr('shopee_census.skip_now_class', { cls: tr(classLabel(reason)) });
  return tr('shopee_census.skip_unknown', { reason: String(reason || '') });
}

/** i18n key of an S2 action. */
export function actionLabel(action) {
  return `shopee_census.action_${APPLY_ACTIONS.indexOf(action) >= 0 ? action : 'other'}`;
}

/** Table rows: everything with showAll, else all but the ok_* rows. */
export function visibleRows(orders, showAll) {
  const list = Array.isArray(orders) ? orders.filter(o => o && typeof o === 'object') : [];
  if (showAll) return list;
  return list.filter(o => OK_CLASSES.indexOf(o.class) < 0);
}

/** Summary chips: [{cls, n}] in CENSUS_CLASSES order, zero counts dropped. */
export function summaryChips(summary) {
  const s = summary && typeof summary === 'object' ? summary : {};
  const out = [];
  CENSUS_CLASSES.forEach(cls => {
    const n = Number(s[cls]) || 0;
    if (n > 0) out.push({ cls, n });
  });
  Object.keys(s).forEach(cls => {
    const n = Number(s[cls]) || 0;
    if (!isKnownClass(cls) && n > 0) out.push({ cls, n });
  });
  return out;
}

/**
 * selectedIds — the bo_ids to send: only selectable rows, unique, > 0, in the
 * order given (whatever the table handed over is re-checked here).
 */
export function selectedIds(rows) {
  const seen = {};
  const out = [];
  (Array.isArray(rows) ? rows : []).forEach(r => {
    const id = Number(r && r.bo_id);
    if (!r || !selectable(r.class) || !(id > 0) || seen[id]) return;
    seen[id] = true;
    out.push(id);
  });
  return out;
}

/** S2 body. dry_run defaults to TRUE: a real write must be asked for. */
export function applyBody(ids, dryRun) {
  return { bo_ids: (Array.isArray(ids) ? ids : []).slice(), dry_run: dryRun !== false };
}

/** S2 response -> {applied, skipped} arrays (never null). */
export function applyResult(data) {
  const d = data && typeof data === 'object' ? data : {};
  return {
    dryRun: d.dry_run !== false,
    applied: Array.isArray(d.applied) ? d.applied : [],
    skipped: Array.isArray(d.skipped) ? d.skipped : []
  };
}

/** The ids a dry run says it WOULD repair — what the real apply sends. */
export function appliedIds(result) {
  const r = applyResult(result);
  const out = [];
  r.applied.forEach(a => {
    const id = Number(a && a.bo_id);
    if (id > 0 && out.indexOf(id) < 0) out.push(id);
  });
  return out;
}

/** Download name of the CSV: shopee_cancel_census_<from>_<to>.csv. */
export function csvFilename(from, to) {
  const clean = s => String(s || '').replace(/[^0-9-]/g, '');
  return `shopee_cancel_census_${clean(from)}_${clean(to)}.csv`;
}

export default {
  CENSUS_CLASSES,
  OK_CLASSES,
  SELECTABLE_CLASSES,
  SKIP_REASONS,
  APPLY_ACTIONS,
  CENSUS_MAX_DAYS,
  APPLY_MAX_IDS,
  classLabel,
  classTone,
  selectable,
  rangeDays,
  censusRangeError,
  censusParams,
  skipReasonLabel,
  actionLabel,
  visibleRows,
  summaryChips,
  selectedIds,
  applyBody,
  applyResult,
  appliedIds,
  csvFilename
};
